import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Play, Heart, Share2, ExternalLink } from "lucide-react";
import { FaSpotify, FaApple, FaYoutube } from "react-icons/fa";
import { http } from "@/lib/api";
import SEO from "@/components/SEO";
import { Section, Reveal } from "@/components/Section";
import ReleaseCard from "@/components/ReleaseCard";
import Artwork from "@/components/Artwork";
import { useMusic } from "@/context/MusicPlayer";

const ReleaseDetail = () => {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [related, setRelated] = useState([]);
  const [lyrics, setLyrics] = useState([]);
  const { play, favorites, toggleFavorite } = useMusic();

  useEffect(() => {
    setR(null);
    http.get(`/releases/${id}`).then((res) => setR(res.data)).catch(() => setR(null));
    http.get("/releases").then((res) => setRelated(res.data.filter((x) => x.id !== id).slice(0, 4)));
    http.get("/lyrics").then((res) => setLyrics(res.data));
  }, [id]);

  const matchedLyric = useMemo(() => {
    if (!r) return null;
    return lyrics.find((l) => l.title === r.title || l.title.startsWith(r.title.split(" (")[0]));
  }, [r, lyrics]);

  if (!r) {
    return <div className="min-h-screen flex items-center justify-center text-white/40">Loading…</div>;
  }

  const isFav = favorites.includes(r.id);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MusicRecording",
    name: r.title,
    alternateName: r.hindi_title,
    inLanguage: ["hi", "sa"],
    genre: r.genre,
    byArtist: { "@type": "MusicGroup", name: "KASHiX" },
    image: r.cover_image,
    description: r.description,
    url: typeof window !== "undefined" ? window.location.href : "",
    sameAs: [r.spotify_url, r.apple_music_url, r.youtube_url].filter(Boolean),
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: `${r.title} — KASHiX`, text: r.description, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch (_e) { /* share cancelled */ }
  };

  return (
    <>
      <SEO title={r.title} description={r.description} image={r.cover_image} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${r.cover_image})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(60px) saturate(120%)", opacity: 0.35 }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/80 to-black" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <Link to="/releases" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/60 hover:text-[#FF9933]">
            <ArrowLeft size={14} /> Back to Music
          </Link>
          <div className="mt-8 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="relative aspect-square overflow-hidden rounded-3xl border border-white/10 shadow-[0_30px_80px_-30px_rgba(255,153,51,0.4)]">
                  <Artwork src={r.cover_image} alt={r.title} className="absolute inset-0 h-full w-full object-cover" rounded="" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7 flex flex-col justify-center">
              <Reveal delay={0.1}>
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Release · {r.year}</div>
                <div className="mt-3 font-hindi text-3xl md:text-5xl text-[#FF9933]">{r.hindi_title}</div>
                <h1 className="mt-2 font-display text-5xl md:text-7xl tracking-tight text-white leading-[0.95]">{r.title}</h1>
                <div className="mt-3 text-[11px] uppercase tracking-[0.35em] text-white/60">{r.genre}{r.duration ? ` · ${r.duration}` : ""}</div>
                <p className="mt-6 text-white/75 leading-relaxed max-w-2xl">{r.description}</p>
                <div className="mt-8 flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => play(r)}
                    data-testid="release-detail-play"
                    className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] hover:shadow-[0_0_30px_rgba(255,153,51,0.55)] transition-all"
                  >
                    <Play size={12} fill="currentColor" /> Play in Bottom Player
                  </button>
                  <button
                    onClick={() => { toggleFavorite(r.id); toast.success(isFav ? "Removed" : "Added to favorites"); }}
                    aria-label={isFav ? "Unfavorite" : "Favorite"}
                    className={`inline-flex items-center gap-2 rounded-full border px-5 py-3 text-xs uppercase tracking-[0.3em] transition ${isFav ? "border-[#FF9933] text-[#FF9933]" : "border-white/15 text-white hover:text-[#FF9933] hover:border-[#FF9933]"}`}
                  >
                    <Heart size={12} fill={isFav ? "currentColor" : "none"} /> Favorite
                  </button>
                  <button onClick={share} className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-5 py-3 text-xs uppercase tracking-[0.3em] hover:text-[#FF9933] hover:border-[#FF9933] transition">
                    <Share2 size={12} /> Share
                  </button>
                </div>
                <div className="mt-8 flex items-center gap-6 text-white/70">
                  <a href={r.spotify_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 hover:text-[#FF9933]">
                    <FaSpotify size={16} /> Spotify <ExternalLink size={11} />
                  </a>
                  <a href={r.apple_music_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 hover:text-[#FF9933]">
                    <FaApple size={16} /> Apple Music <ExternalLink size={11} />
                  </a>
                  <a href={r.youtube_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 hover:text-[#FF9933]">
                    <FaYoutube size={16} /> YouTube <ExternalLink size={11} />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SPOTIFY EMBED */}
      <Section className="!py-14">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933] mb-4">Listen · Spotify</div>
          <div className="glass rounded-2xl overflow-hidden border border-[#FF9933]/20">
            <iframe
              title={`Spotify — ${r.title}`}
              src={r.spotify_embed_url || "https://open.spotify.com/embed/artist/3CVKryef8hUWE97XMj2cw1?theme=0"}
              width="100%"
              height="380"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="block bg-transparent"
            />
          </div>
        </Reveal>
      </Section>

      {/* YOUTUBE CARD */}
      <Section className="!py-14">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933] mb-4">Watch · YouTube</div>
          <a href={r.youtube_url} target="_blank" rel="noreferrer noopener" className="group relative block aspect-video overflow-hidden rounded-2xl border border-white/5">
            <Artwork src={r.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" rounded="" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[#FF9933] flex items-center justify-center shadow-[0_0_60px_rgba(255,153,51,0.5)] group-hover:scale-110 transition-transform">
                <Play size={26} fill="black" className="translate-x-1 text-black" />
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 p-6 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Watch on YouTube</div>
                <div className="font-display text-2xl text-white mt-1">{r.title}</div>
              </div>
              <ExternalLink className="text-white/70" size={16} />
            </div>
          </a>
        </Reveal>
      </Section>

      {/* CREDITS + LYRICS LINK */}
      <Section className="!py-14">
        <div className="grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="glass rounded-2xl p-8 h-full">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Credits</div>
              <p className="mt-3 text-white/80">{r.credits || "Vocals, Composition, Production — KASHiX"}</p>
              <div className="mt-4 text-xs text-white/50">© {r.year} KASHiX. All rights reserved.</div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass rounded-2xl p-8 h-full">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Lyrics · Meaning</div>
              {matchedLyric ? (
                <>
                  <p className="mt-3 text-white/80 line-clamp-3 font-editorial text-lg">
                    “{matchedLyric.english_translation.split(".")[0]}.”
                  </p>
                  <Link
                    to={`/lyrics/${matchedLyric.id}`}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-5 py-2 text-[10px] uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] transition"
                  >
                    Read Lyrics
                  </Link>
                </>
              ) : (
                <p className="mt-3 text-white/60">Lyrics coming soon.</p>
              )}
            </div>
          </Reveal>
        </div>
      </Section>

      {/* RELATED */}
      {related.length > 0 && (
        <Section className="!py-16">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">More from KASHiX</div>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-white tracking-tight">You might also love</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((rel, i) => <ReleaseCard key={rel.id} release={rel} index={i} list={related} />)}
          </div>
        </Section>
      )}

      {/* Support */}
      <Section className="!py-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl glass-strong p-10 text-center">
            <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(60% 80% at 50% 0%, rgba(255,153,51,0.25), transparent 70%)" }} />
            <div className="relative">
              <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight">If this moved you, fuel the next release.</h3>
              <Link to="/support" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] transition">
                Support KASHiX
              </Link>
            </div>
          </div>
        </Reveal>
      </Section>
    </>
  );
};

export default ReleaseDetail;
